import React, { useState } from 'react';
import { MessageCircle, X, Send, User } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useGeo } from '../contexts/GeoContext';

const SupportWidget = () => {
  const { user, isAuthenticated } = useAuth();
  const { isBrazil } = useGeo();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error(isBrazil ? 'Preencha todos os campos' : 'Fill in all fields');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSent(true);
        toast.success(isBrazil ? 'Mensagem enviada!' : 'Message sent!', {
          description: isBrazil ? 'Responderemos em breve' : 'We will reply shortly'
        });
        setTimeout(() => {
          setIsOpen(false);
          setSent(false);
          setFormData({ name: '', email: '', message: '' });
        }, 3000);
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error(isBrazil ? 'Erro ao enviar mensagem' : 'Error sending message');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => {
            if (isAuthenticated) {
              window.location.href = '/suporte';
            } else {
              setIsOpen(true);
            }
          }}
          className="fixed bottom-24 sm:bottom-6 right-4 sm:right-6 z-40 bg-red-600 hover:bg-red-700 text-white rounded-full p-3.5 sm:p-4 shadow-2xl hover:scale-110 transition-all group"
          data-testid="support-widget-button"
        >
          {isAuthenticated ? <User className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
          <span className="absolute top-full right-0 mt-2 bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            {isAuthenticated ? (isBrazil ? '👤 Suporte VIP' : '👤 VIP Support') : (isBrazil ? '💬 Fale conosco!' : '💬 Chat with us!')}
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 sm:bottom-6 right-4 sm:right-6 z-40 w-96 max-w-[calc(100vw-3rem)]">
          <Card className="bg-gray-900 border-2 border-red-900/30 shadow-2xl">
            <CardHeader className="border-b border-red-900/30 bg-gradient-to-r from-red-600 to-red-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0D1229]"></span>
                  </div>
                  <div>
                    <CardTitle className="text-white text-lg">{isBrazil ? 'Suporte Online' : 'Online Support'}</CardTitle>
                    <p className="text-white/80 text-xs flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      {isBrazil ? 'Atendimento ativo' : 'Active support'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-white/10 rounded-lg p-1 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {sent ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">{isBrazil ? 'Mensagem Enviada!' : 'Message Sent!'}</h3>
                  <p className="text-gray-400 text-sm">{isBrazil ? 'Responderemos em breve' : 'We will reply shortly'}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">{isBrazil ? 'Nome' : 'Name'}</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-gray-800 border border-red-900/30 rounded-lg px-4 py-2 text-white focus:border-red-600 focus:outline-none"
                      placeholder={isBrazil ? 'Seu nome' : 'Your name'}
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-gray-800 border border-red-900/30 rounded-lg px-4 py-2 text-white focus:border-red-600 focus:outline-none"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">{isBrazil ? 'Mensagem' : 'Message'}</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={4}
                      className="w-full bg-gray-800 border border-red-900/30 rounded-lg px-4 py-2 text-white focus:border-red-600 focus:outline-none resize-none"
                      placeholder={isBrazil ? 'Como podemos ajudar?' : 'How can we help?'}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3"
                  >
                    {sending ? (
                      isBrazil ? '⏳ Enviando...' : '⏳ Sending...'
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {isBrazil ? 'Enviar Mensagem' : 'Send Message'}
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default SupportWidget;
